import { Outlet, useNavigate, useLocation } from "react-router-dom";
import PageTransition from "@/shared/components/PageTransition";
import { SidebarProvider, SidebarTrigger } from "@/shared/components/ui/sidebar";
import AppSidebar from "@/shared/components/AppSidebar";
import { useFullscreenWriting } from "@/shared/contexts/FullscreenWritingContext";
import { useAuth } from "@/shared/hooks/useAuth";
import { useSindirepaAccess } from "@/shared/hooks/useSindirepaAccess";
import { useSessionTracking } from "@/shared/hooks/useSessionTracking";
import { useActivityLogger } from "@/shared/hooks/useActivityLogger";
import { Button } from "@/shared/components/ui/button";
import { LogOut, Loader2 } from "lucide-react";
import MessageInbox from "@/shared/components/messages/MessageInbox";
import ChatOperacionalPanel from "@/shared/components/chat/ChatOperacionalPanel";
import HeaderCalendar from "@/shared/components/HeaderCalendar";
import HeaderCalculator from "@/shared/components/HeaderCalculator";
import HeaderClockWeather from "@/shared/components/HeaderClockWeather";
import { useEffect, useState, useRef, Suspense } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import ClosingDeadlineAlert from "@/shared/components/finance/ClosingDeadlineAlert";
import FloatingAssistant from "@/shared/components/FloatingAssistant";
import UserQualificationBadge, { type UserQualification } from "@/shared/components/users/UserQualificationBadge";
// PortalToggle removed — internal users no longer switch portals from header

const DashboardLayout = () => {
  const { user, signOut } = useAuth();
  const { isSindirepaOnly, loading: sindirepaLoading } = useSindirepaAccess();
  const { fullscreen: writingFullscreen } = useFullscreenWriting();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  /** Alerta de prazo de fechamento mensal — apenas rotas do financeiro (evita ruído em Jurídica, IA, etc.). */
  const showFinanceClosingAlert =
    pathname.includes("/dashboard/finance-home") ||
    /^\/dashboard\/finance(\/|$)/.test(pathname) ||
    pathname.includes("/dashboard/financeiro");
  const { recordLogout } = useSessionTracking();
  const { logActivity } = useActivityLogger();
  const [profileName, setProfileName] = useState<string | null>(null);
  const [qualificacao, setQualificacao] = useState<string | null>(null);
  const lastLoggedPath = useRef<string>("");
  const [chatOpen, setChatOpen] = useState(false);

  // Log page navigation
  useEffect(() => {
    if (!user || !location.pathname) return;
    // Only log meaningful navigations, not repeats
    if (lastLoggedPath.current === location.pathname) return;
    lastLoggedPath.current = location.pathname;

    const MODULE_MAP: Record<string, string> = {
      "/dashboard": "dashboard",
      "/dashboard/finance": "financeiro",
      "/dashboard/juridica": "gestão jurídica",
      "/dashboard/juridica/formatacao": "formatação",
      "/dashboard/juridica/formatacao/editor": "editor de formatação",
      "/dashboard/juridica/documentos": "documentos",
      "/dashboard/calculos": "cálculos",
      "/dashboard/juridica/calculos": "cálculos",
      "/dashboard/juridica/processos": "processos",
      "/dashboard/administrativa": "gestão administrativa",
      "/dashboard/administrativa/normas": "normas",
      "/dashboard/administrativa/sistema": "sistema",
      "/dashboard/administrativa/admin": "administração",
      "/dashboard/administrativa/configuracoes": "configurações",
      "/dashboard/administrativa/admin/permissoes": "permissões",
      "/dashboard/administrativa/admin/usuarios": "usuários",
      "/dashboard/administrativa/relatorios": "relatórios",
      "/dashboard/administrativa/manual": "manual",
      "/dashboard/administrativa/roadmap": "roadmap",
    };

    // Find the best matching module
    const sortedKeys = Object.keys(MODULE_MAP).sort((a, b) => b.length - a.length);
    const matchedKey = sortedKeys.find((key) => location.pathname.startsWith(key));
    const module = matchedKey ? MODULE_MAP[matchedKey] : "sistema";

    logActivity({
      module,
      action: "Navegação",
      details: `Acessou: ${location.pathname}`,
    });
  }, [location.pathname, user, logActivity]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        // Tenta usar cache do usePermissions (mesmo SELECT) para não duplicar
        const cached = (globalThis as any).__ferruginiProfileCache as
          | { userId: string; nome?: string; qualificacao?: string; ts: number }
          | undefined;
        if (cached && cached.userId === user.id && Date.now() - cached.ts < 120_000 && cached.nome) {
          if (!cancelled) {
            setProfileName(cached.nome);
            setQualificacao(cached.qualificacao ?? null);
          }
          return;
        }
        const { data } = await supabase
          .from("profiles")
          .select("nome, qualificacao")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!cancelled && data) {
          setProfileName(data.nome);
          setQualificacao(data.qualificacao);
          (globalThis as any).__ferruginiProfileCache = { userId: user.id, nome: data.nome, qualificacao: data.qualificacao, ts: Date.now() };
        }
      } catch (err) {
        console.error("[DashboardLayout] profile fetch failed:", err);
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, [user?.id]);  

  const handleSignOut = async () => {
    await recordLogout("manual");
    await signOut();
    navigate("/");
  };

  if (sindirepaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Layout reduzido legado (hoje `useSindirepaAccess` não ativa este ramo). institution_external
  // na própria instituição passa pelo InternalGuard e chega aqui com sidebar completa.
  if (isSindirepaOnly) {
    return (
      <div className="min-h-screen flex flex-col w-full bg-[var(--fg-mesh-bg)] dark:[background-image:var(--mesh-gradient)]">
        <header className="h-14 shrink-0 flex items-center justify-between px-4 md:px-6 gap-3 fg-header-glass sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-secondary/60" />
            <span className="text-sm font-semibold text-foreground font-display tracking-wide drop-shadow-sm">SINDIREPA</span>
          </div>
          <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-primary transition-colors" onClick={handleSignOut} title="Sair">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <main className="flex-1 p-6 min-h-0 overflow-auto bg-[#f9f6f0]">
          <PageTransition>
            <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}>
              <Outlet />
            </Suspense>
          </PageTransition>
        </main>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div
        className={
          writingFullscreen
            ? "fixed inset-0 flex flex-col z-[60] bg-background h-[100dvh] min-h-[100dvh]"
            : "h-[100dvh] max-h-[100dvh] flex w-full overflow-hidden"
        }
      >
        {!writingFullscreen && <AppSidebar />}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-x-hidden">
          {!writingFullscreen && (
            <header className="h-14 shrink-0 flex items-center justify-between px-3 sm:px-4 md:px-6 gap-2 sm:gap-3 fg-header-glass sticky top-0 z-30 relative min-h-[44px]">
              {/* Gold shimmer bottom line */}
              <div className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-gold-shimmer opacity-60 pointer-events-none" aria-hidden />
              <div className="flex items-center gap-3 min-w-0">
                <SidebarTrigger className="shrink-0 text-muted-foreground hover:text-primary transition-colors" />
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-1 h-4 rounded-full bg-secondary/60" />
                  <span className="text-sm font-semibold text-foreground font-accent tracking-widest uppercase text-[11px] drop-shadow-sm">
                    Sistema Ferrugini
                  </span>
                </div>
              </div>
              <HeaderClockWeather className="flex-1 justify-center min-w-0 overflow-hidden" />
              {/* shrink-0: evita que o flex encolha os ícones (chat, caixa, etc.) a largura zero em viewports estreitas */}
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 max-w-[min(100vw-12rem,280px)]">
                  <span className="text-sm font-medium text-foreground truncate max-w-[120px] md:max-w-[200px] font-body drop-shadow-sm">
                    {profileName || (qualificacao === "socio_gestor" ? "Alencar Ferrugini" : user?.email)}
                  </span>
                  {qualificacao && (
                    <span className="shrink-0">
                      <UserQualificationBadge qualification={qualificacao as UserQualification} size="sm" />
                    </span>
                  )}
                </div>
                <div className="h-5 w-px bg-border/40 hidden md:block shrink-0" aria-hidden />
                <ChatOperacionalPanel open={chatOpen} onOpenChange={setChatOpen} />
                <MessageInbox />
                <HeaderCalendar />
                <HeaderCalculator />
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                  onClick={handleSignOut}
                  title="Sair"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </header>
          )}
          {!writingFullscreen && showFinanceClosingAlert && <ClosingDeadlineAlert />}
          <main
            className={
              writingFullscreen
                ? "flex-1 min-h-0 min-w-0 overflow-hidden p-0 flex flex-col"
                : "flex-1 p-4 sm:p-6 min-h-0 min-w-0 overflow-auto bg-[#f9f6f0] overflow-x-hidden"
            }
          >
            <PageTransition
              wrapperClassName={
                writingFullscreen ? "flex-1 min-h-0 min-w-0 flex flex-col [&>*]:min-h-0" : undefined
              }
            >
              {/* Suspense local: preserva sidebar/header durante o lazy-load do módulo (chunks grandes como instituicoes-chat). */}
              <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}>
                <Outlet />
              </Suspense>
            </PageTransition>
          </main>
        </div>
        {!writingFullscreen && <FloatingAssistant />}
      </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
